import { useEffect } from 'react';
import { isNotNil } from '~/utilities/type-guards';
import { noOpFn } from '~/utilities/util';
import type { GetTargetFn } from '~/onboarding/types';
import { TARGET_CLICK } from '../components/Tooltip/types';

export type UseTargetInteractionCallbackFn = (event: PointerEvent) => void;

export const useTargetInteraction = (
  /**
   * A function that will be called when the target is clicked or focused
   */
  callback: UseTargetInteractionCallbackFn,
  /**
   * A function that returns an element to be used with the callback function.
   * 1. A 'click' and 'focus' event listener will be attached to the target
   * 2. The callback function will be called when the target is clicked or focused
   * 3. A 'TargetClick' custom event will be dispatched on the target element
   */
  getTarget?: GetTargetFn,
): void => {
  useEffect(() => {
    const target = getTarget?.();
    const onTargetInteraction = (event: PointerEvent) => {
      callback(event);

      if (target) {
        const targetClickEvent = new CustomEvent(TARGET_CLICK, {
          bubbles: true,
          cancelable: true,
        });

        target.dispatchEvent(targetClickEvent);
      }
    };

    if (isNotNil(target)) {
      target.addEventListener('click', onTargetInteraction);
      target.addEventListener('focus', onTargetInteraction);

      return () => {
        target.removeEventListener('click', onTargetInteraction);
        target.removeEventListener('focus', onTargetInteraction);
      };
    }

    return noOpFn;
  }, [callback, getTarget]);
};
